import { getWebPath } from '@doss/common-all'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import { compact, orderBy } from 'lodash'
import { MouseEvent, useMemo } from 'react'
import Highlighter from 'react-highlight-words'
import { RxArrowTopRight } from 'react-icons/rx'
import { TbColumns } from 'react-icons/tb'
import Typography from '~/@/components/ui/typography'
import { amber } from '~/common/styles/colors'
import { escapeRegExp } from '~/common/utils/lookups'
import {
  resolveLookupEnumRendering,
  useLookupContext,
} from '~/components/records/lookup/hooks/LookupProvider'
import { LookupTableRow, LookupTableRowCell } from '~/components/records/types'
import { EnumChip } from '~/components/shared/EnumChip'
import { EnumOptionMark } from '~/components/shared/EnumOptionMark'
import { LookupEnumRendering } from '~/components/shared/lookup-enum'
import { BreadcrumbObjectType } from '../../../../common/generated/apollo-operations'

dayjs.extend(utc)

interface CellPreview {
  display: string | null
  enumRendering: LookupEnumRendering | null
}

interface PreviewCellValueProps {
  display: string
  enumRendering: LookupEnumRendering | null
  searchQuery: string
}

/**
 * A preview cell's value, rendered the way it is in the source table: a multi-select as tinted
 * chips, a single-select as a marked label, everything else as plain highlighted text.
 */
const PreviewCellValue = ({ display, enumRendering, searchQuery }: PreviewCellValueProps) => {
  if (enumRendering?.kind === 'chips') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {enumRendering.values.map((enumValue) => (
          <EnumChip
            key={enumValue.label}
            color={enumValue.color}
            iconName={enumValue.iconName}
            label={enumValue.label}
          />
        ))}
      </div>
    )
  }

  const highlightedDisplay = (
    <Typography variant="body3">
      <Highlighter
        autoEscape={true}
        highlightStyle={{
          backgroundColor: amber[5],
          color: amber[12],
        }}
        searchWords={[searchQuery]}
        textToHighlight={display}
      />
    </Typography>
  )

  if (!enumRendering?.color && !enumRendering?.iconName) {
    return highlightedDisplay
  }

  return (
    <div className="flex items-center gap-2">
      <EnumOptionMark color={enumRendering.color} iconName={enumRendering.iconName} />
      {highlightedDisplay}
    </div>
  )
}

interface LookupMenuItemPreviewProps {
  searchQuery: string
  option: LookupTableRow
  columnId: string
  parentRowId?: string
}

/**
 * Preview of a lookup menu item that shows the name of the option and sorted cells with higlighting
 */
export const LookupMenuItemPreview = ({
  searchQuery,
  option,
  columnId,
  parentRowId,
}: LookupMenuItemPreviewProps) => {
  const { getCellDisplay, getEnumSource, isUnauth } = useLookupContext()

  /**
   * We always show the current queried cell first followed by the rest of the cells sorted
   * by the number of matches with the searchQuery.
   * The lookup column cell is marked with a blue accent border and a LOOKUP badge to make it
   * visually clear which field is being queried.
   */

  const visibleCells = useMemo(() => {
    const currentColumnCell = option.cells.find((cell) => cell.column.id === columnId)
    const remainingCells = option.cells.filter((cell) => cell.id !== currentColumnCell?.id)

    // Count the number of matches in each cell's content with the searchQuery
    const countMatches = (cell: LookupTableRowCell) => {
      return searchQuery
        ? (cell.userVisibleValue?.match(new RegExp(escapeRegExp(searchQuery), 'gi')) || []).length
        : 0
    }

    const sortedRemainingCells = orderBy(
      remainingCells,
      [countMatches, (cell) => cell.column.order],
      ['desc', 'asc'],
    )

    const orderedCells = [currentColumnCell, ...sortedRemainingCells].filter((cell) => cell)

    return compact(orderedCells)
  }, [columnId, option.cells, searchQuery])

  // Each cell's display string and how its value renders
  const cellPreviews = useMemo(() => {
    const previews = new Map<string, CellPreview>()
    visibleCells.forEach((cell) => {
      const display = getCellDisplay(cell, cell.column.id)
      previews.set(cell.id, {
        display,
        enumRendering: resolveLookupEnumRendering({
          enumSource: getEnumSource(cell, cell.column.id),
          label: display ?? '',
          cell,
        }),
      })
    })
    return previews
  }, [getCellDisplay, getEnumSource, visibleCells])

  const handleNavigate = (event: MouseEvent<HTMLElement>) => {
    if (isUnauth) {
      return
    }

    // If creating a child lookup on parent, route the user to the record
    if (parentRowId) {
      event.stopPropagation()
      const path = getWebPath({
        objectType: BreadcrumbObjectType.TABLE_ROW,
        objectId: parentRowId,
      })
      window.open(path, '_blank')
    }

    event.stopPropagation()
    const path = getWebPath({
      objectType: BreadcrumbObjectType.TABLE_ROW,
      objectId: option.id,
    })
    window.open(path, '_blank')
  }

  return (
    <div className="flex flex-col gap-4">
      <div
        className={`flex gap-1 rounded p-2 ps-1 ${!isUnauth ? 'group/lookup cursor-pointer hover:bg-gray-2' : 'pointer-events-none'}`}
        onClick={handleNavigate}
      >
        <span className="relative size-4">
          <RxArrowTopRight className="absolute opacity-0 transition-opacity duration-300 group-hover/lookup:opacity-100" />
          <TbColumns className="absolute transition-opacity duration-300 group-hover/lookup:opacity-0" />
        </span>
        <Typography
          className="min-w-0 break-words leading-none group-hover/lookup:underline"
          variant="body2"
        >
          <Highlighter
            autoEscape={true}
            highlightStyle={{
              backgroundColor: amber[5],
              color: amber[12],
            }}
            searchWords={[searchQuery]}
            textToHighlight={option.name}
          />
        </Typography>
      </div>
      <div className="flex flex-col justify-between gap-4 ps-1">
        {visibleCells.map((cell) => {
          const isLookupColumn = cell.column.id === columnId
          const preview = cellPreviews.get(cell.id)
          return (
            <div
              key={cell.column.id}
              className={`w-full grow whitespace-normal break-words ${isLookupColumn ? 'border-l-2 border-blue-8 pl-2' : ''}`}
            >
              <div className="flex items-center gap-1 !text-[10px] text-gray-9">
                <Typography
                  className={`!text-[10px] ${isLookupColumn ? 'text-blue-10' : 'text-gray-9'}`}
                >
                  {cell.column.name}
                </Typography>
                {isLookupColumn && (
                  <span className="rounded bg-blue-1 px-1 py-0.5 text-[10px] leading-none text-blue-10">
                    LOOKUP
                  </span>
                )}
              </div>

              <div>
                {preview?.display ? (
                  <PreviewCellValue
                    display={preview.display}
                    enumRendering={preview.enumRendering}
                    searchQuery={searchQuery}
                  />
                ) : (
                  <Typography className="text-gray-5" variant="body3">
                    --
                  </Typography>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
